import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useFetchWithSpinner } from "../../hooks/useFetchWithSpinner";
import {
  BarChartSummary,
  BarChartComparison,
  VerticalPositiveNegativeBarChart,
  LineChartComparison
} from "../Charts";
import { selectDates, selectTopN, selectComparisonYear } from "../../redux/slices/filtersSlice";
import {
  filterData,
  processFilteredData,
  groupAgeByRange,
  applyFilterLogic
} from "../../utils/filterFunctions";
import "./Pages.css";

const DelitosSexuales = () => {
  const [data, setData] = useState([]);

  const dates = useSelector(selectDates);
  const comparisonYear = useSelector(selectComparisonYear);
  const topN = useSelector(selectTopN);

  const baseFilters = {
    sexo: null,
    estado_civil: null,
    comuna: null,
    parentesco: null,
    rangoEdad: null,
    mes: null,
    articulo: null,
  };
  const [filters, setFilters] = useState({ ...baseFilters });
  const [frozenKey, setFrozenKey] = useState(null);
  const fetchWithSpinner = useFetchWithSpinner();

  useEffect(() => {
    if (dates.start && dates.end) {
      fetchWithSpinner({
        startDate: dates.start,
        endDate: dates.end,
        year_to_compare: comparisonYear,
        endpoint: 'delitosSexuales/',
      }, setData);
    }
  }, [dates.start, dates.end, comparisonYear]);

  const currentYear = dates.end ? new Date(dates.end).getFullYear() : new Date().getFullYear();

  const filteredData = filterData(data, filters);

  const results = processFilteredData(filteredData, {
    includeTotals: true,
    groupByFields: ["mes", "sexo", "comuna", "articulo"]
  }, comparisonYear, currentYear);

  const { totals: { total1, total2, diferencia, variacion }, resumenData } = results;
  const mesData = results.groups.mes;
  const sexoData = results.groups.sexo.filter(item => item.name === "Hombre" || item.name === "Mujer").sort((a, b) => (b[currentYear] + b[comparisonYear]) - (a[currentYear] + a[comparisonYear]));
  const comunaData = results.groups.comuna.sort((a, b) => (b[currentYear] + b[comparisonYear]) - (a[currentYear] + a[comparisonYear]));
  const articulosDataRaw = results.groups.articulo.sort((a, b) => (b[currentYear] + b[comparisonYear]) - (a[currentYear] + a[comparisonYear]));
  const articulosData = articulosDataRaw.slice(0, Math.min(topN, articulosDataRaw.length));


  const ranges = [
    { label: "Blank", min: null, max: null },
    { label: "0-5", min: 0, max: 5 },
    { label: "6-11", min: 6, max: 11 },
    { label: "12-13", min: 12, max: 13 },
    { label: "14-17", min: 14, max: 17 },
    { label: "18-24", min: 18, max: 24 },
    { label: "25-28", min: 25, max: 28 },
    { label: "29-32", min: 29, max: 32 },
    { label: "33-38", min: 33, max: 38 },
    { label: "39-45", min: 39, max: 45 },
    { label: "46-52", min: 46, max: 52 },
    { label: "53-59", min: 53, max: 59 },
    { label: "60-66", min: 60, max: 66 },
    { label: "Mayor-67", min: 67, max: Infinity },
    { label: "Sin Dato", min: null, max: null },
  ];
  const edadData = groupAgeByRange(filteredData, ranges, comparisonYear, currentYear).sort((a, b) => (b[currentYear] + b[comparisonYear]) - (a[currentYear] + a[comparisonYear]));

  const handleFilterClick = (tipo, valor, source) => {
    setFilters((prevFilters) =>
      applyFilterLogic(prevFilters, tipo, valor, source, frozenKey, setFrozenKey, baseFilters)
    );
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-grid">
        <div className="row top">
          <div className="card small">
            <BarChartSummary
              resumenData={resumenData}
              diferencia={diferencia}
              variacion={variacion}
              xKey="name"
              title="Resumen general"
              selectedKey={filters.año}
              frozenKey={frozenKey}
              onBarClick={(d) => handleFilterClick("año", d?.category, "Resumen general")}
            />
          </div>
          <div className="card small">
            <VerticalPositiveNegativeBarChart showData={sexoData} xKey="name" title="Sexo" selectedKey={filters.sexo} frozenKey={frozenKey} onBarClick={(d) => handleFilterClick("sexo", d?.category, "Sexo")} comparisonYear={comparisonYear} />
          </div>
          <div className="card wide">
            <LineChartComparison
              showData={mesData}
              xKey="name"
              title="Victimas por mes"
              selectedKey={filters.mes}
              frozenKey={frozenKey}
              onBarClick={(d) => handleFilterClick("mes", d?.category, "Victimas por mes")}
            />
          </div>
        </div>
        <div className="row middle">
          <div className="card tall">
            <VerticalPositiveNegativeBarChart
              showData={edadData}
              xKey="rango"
              title="Rango de edad"
              selectedKey={filters.rangoEdad}
              frozenKey={frozenKey}
              onBarClick={(d) => handleFilterClick("rangoEdad", d?.category, "Rango de edad")}
              height={750}
              comparisonYear={comparisonYear}
            />
          </div>
          <div className="column-right">
            <div className="card medium">
              <BarChartComparison
                showData={comunaData}
                xKey="name"
                title="Victimas por comuna"
                selectedKey={filters.comuna}
                frozenKey={frozenKey}
                onBarClick={(d) => handleFilterClick("comuna", d?.category, "Victimas por comuna")}
              />
            </div>
            <div className="card medium">
              <BarChartComparison
                showData={articulosData}
                xKey="name"
                title="Top Articulos"
                selectedKey={filters.articulo}
                frozenKey={frozenKey}
                onBarClick={(d) => handleFilterClick("articulo", d?.category, "Top Articulos")}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DelitosSexuales;
